// The picture of every kit root (public/images/dhatu/<file>.jpg): one person doing the action, in the folk-art style
// of the story illustrations. `scene` is what the picture shows, used as its alt text with the root's meaning.

interface DhatuPictureEntry {
    src: string
    scene: string
}

const ROWS: [root: string, file: string, scene: string][] = [
    ['पढ', 'padha', "A boy sitting cross-legged reading an open book."],
    ['पास', 'paasa', "A girl shading her eyes with one hand, looking far away at birds over the hills."],
    ['गच्छ', 'gaccha', "A traveller with a cloth bundle on a stick walking away along a village road."],
    ['इच्छ', 'iccha', "A girl with clasped hands looking up hopefully at a shooting star in the evening sky."],
    ['खेल', 'khela', "Three children playing happily with a ball in a courtyard."],
    ['सुण', 'suna', "A child cupping a hand to his ear, listening to a bird singing on a branch."],
    ['भुंज', 'bhunja', "A family sitting on the floor eating a meal from banana leaves."],
    ['पुच्छ', 'puccha', "A student raising her hand to ask her teacher a question."],
    ['कह', 'kaha', "A grandmother telling a story to two small children sitting around her."],
    ['खण', 'khana', "A man digging the earth with a spade."],
    ['कर', 'kara', "A busy woman doing housework, sweeping the courtyard with a broom."],
    ['गिण्ह', 'ginha', "A boy taking a mango from a basket with both hands."],
    ['नम', 'nama', "A young man bowing with folded hands in namaste before an elder."],
    ['जाण', 'jaana', "A wise teacher calmly explaining, one hand raised, an open palm-leaf manuscript beside him."],
    ['धाव', 'dhaava', "A boy running fast across a field, hair flying."],
    ['हस', 'hasa', "A girl laughing joyfully with her eyes closed."],
    ['णच्च', 'nacca', "A girl dancing a classical dance of Bharat with graceful hand gestures."],
    ['सेव', 'seva', "A young man serving a cup of water to an old man."],
    ['सय', 'saya', "A child sleeping peacefully on a mat under a starry night window."],
    ['अच्च', 'acca', "A woman offering flowers and a small oil lamp at a simple stone shrine."],
    ['चित', 'cita', "A girl sitting with her chin on her hand, deep in thought."],
    ['चल', 'cala', "An old man walking slowly with a wooden stick."],
    ['भम', 'bhama', "A wanderer roaming through a forest, looking around curiously."],
    ['जय', 'jaya', "A runner breaking the finish ribbon of a village race, arms raised in victory."],
    ['लिह', 'liha', "A girl writing on a wooden slate with chalk, no readable letters."],
    ['वस', 'vasa', "A family standing in front of their small home with a thatched roof."],
    ['बंध', 'bandha', "A farmer tying a cow to a wooden post with a rope."],
    ['पिव', 'piva', "A boy drinking water from a clay pot."],
    ['भण', 'bhana', "A student reciting aloud from a book held in both hands."],
    ['आगच्छ', 'aagaccha', "A smiling friend coming towards the viewer and waving."],
    ['पेस', 'pesa', "A girl handing a folded letter to a messenger on a horse."],
    ['कीण', 'kiina', "A woman buying vegetables, handing coins to a vendor."],
    ['जिण', 'jina', "A climber planting a plain saffron flag on top of a mountain peak."],
    ['बीह', 'biiha', "A frightened child hiding behind a tree trunk, a snake on the path."],
    ['कंद', 'kanda', "A small child crying with tears, sitting on the ground."],
    ['पाल', 'paala', "A shepherd with a staff protecting his flock of sheep."],
    ['सीख', 'siikha', "A teacher guiding a child's hand as the child learns to draw on a slate."],
    ['जंप', 'janpa', "A lively child chattering excitedly to a friend, hands moving."],
    ['दह', 'daha', "A fire burning brightly in a clay hearth."],
    ['णिम्म', 'nimma', "A potter shaping a clay pot on a potter's wheel."],
    ['तुल', 'tula', "A shopkeeper weighing grain on a hanging balance scale."],
    ['घोस', 'ghosa', "A village crier standing on a platform calling out an announcement, one hand raised."],
    ['फास', 'phaasa', "A child gently touching the head of a calf."],
    ['गज्ज', 'gajja', "A lion roaring on a rock."],
    ['थुण', 'thuna', "A devotee singing praise with both hands raised, eyes closed."],
    ['कलह', 'kalaha', "Two children quarrelling, pointing at each other with angry faces."],
    ['लज्ज', 'lajja', "A shy girl hiding half her face behind the end of her scarf."],
    ['उड्ड', 'udda', "A flock of parrots flying across the sky."],
    ['जग्ग', 'jagga', "A girl waking up and stretching her arms on her mat at dawn."],
    ['तर', 'tara', "A boy swimming in a calm river."],
    ['कस्स', 'kassa', "A farmer ploughing a field with two oxen."],
    ['खम', 'khama', "An elder kindly hugging a boy who is saying sorry."],
    ['पच', 'paca', "A woman cooking in a pot on a clay stove."],
    ['दुह', 'duha', "A woman milking a cow into a brass pot."],
    ['तूस', 'tuusa', "A woman smiling with delight, hands pressed together, receiving good news."],
    ['विरम', 'virama', "A boy stopping on a path, one palm raised to say stop."],
    ['सोह', 'soha', "The bright sun shining over green hills."],
    ['पड', 'pada', "A ripe mango falling from a tree branch."],
    ['तव', 'tava', "A sage sitting very still in penance under a banyan tree."],
    ['धोव', 'dhova', "A woman washing clothes on the steps of a river."],
    ['पविस', 'pavisa', "A boy stepping in through the doorway of a house."],
    ['पलाय', 'palaaya', "A frightened rabbit running away from a barking dog."],
    ['पीस', 'piisa', "A woman grinding grain on a round stone hand mill."],
    ['पेच्छ', 'peccha', "A girl gazing in wonder at a dancing peacock."],
    ['हिंस', 'hinsa', "A careless boy trampling a bed of flowers, crushed flowers under his feet."],
    ['रूस', 'ruusa', "An angry child with crossed arms, frowning and turned away."],
    ['धर', 'dhara', "A girl holding a water pot on her hip."],
    ['मग्ग', 'magga', "A boy holding out his hands asking his mother for a sweet."],
    ['मुंच', 'munca', "A girl opening her hands to release a small bird into the sky."],
    ['बोल्ल', 'bolla', "Two friends sitting on a bench talking to each other."],
    ['मन्न', 'manna', "A girl with her hand on her heart, listening trustingly to her grandmother."],
    ['हव', 'hava', "A calm child standing in a sunny meadow."],
    ['ताड', 'taada', "A drummer beating a large dhol drum with sticks."],
    ['हण', 'hana', "A farmer swatting a mosquito with his hand, a funny moment."],
    ['वड्ढ', 'vaddha', "A young plant growing tall with fresh new leaves in a clay pot."],
    ['अच्छ', 'accha', "An old man resting at home, sitting on a woven cot."],
    ['रम', 'rama', "Children enjoying themselves on a swing hanging from a tree."],
    ['निन्द', 'ninda', "An elder wagging a finger at a boy standing next to a broken pot."],
    ['लंभ', 'lanbha', "A girl receiving a basket of fruit as a gift, delighted."],
    ['सर', 'sara', "An old woman with closed eyes remembering, gently holding a bangle."],
    ['हरिस', 'harisa', "Villagers rejoicing with raised hands at a golden harvest."],
    ['उज्जम', 'ujjama', "A boy pushing a heavy cart up a hill with great effort."],
    ['उवदिस', 'uvadisa', "A guru teaching three disciples under a tree."],
    ['उट्ठ', 'uttha', "A man rising from his seat at sunrise."],
    ['सिंच', 'sinca', "A girl watering plants with a small brass pot."],
    ['चक्ख', 'cakkha', "A boy tasting food from a wooden spoon."],
    ['सिव्व', 'sivva', "A woman sewing cloth with a needle and thread."],
    ['जुज्झ', 'jujjha', "Two wrestlers grappling in a mud wrestling pit."],
    ['णीसर', 'niisara', "A child coming out of a hut doorway into bright sunlight."],
    ['पसंस', 'pasansa', "A teacher clapping and praising a proud student."],
    ['मुण', 'muna', "A thoughtful scholar holding a palm-leaf manuscript, nodding with understanding."],
    ['विक्कीण', 'vikkiina', "A vendor selling colourful vegetables from a market stall."],
    ['चिण', 'cina', "A girl picking flowers into a small basket."],
    ['दा', 'daa', "A man giving a fruit to a small child."],
    ['गा', 'gaa', "A girl singing, playing a tanpura."],
    ['खा', 'khaa', "A child happily eating a ripe mango."],
    ['झा', 'jhaa', "A person meditating cross-legged with closed eyes."],
    ['पा', 'paa', "A girl drinking milk from a brass cup."],
    ['ठा', 'thaa', "A boy standing straight and still by a gate."],
    ['ण्हा', 'nhaa', "A child bathing at a river, pouring water from a pot over the head."],
    ['णे', 'ne', "A man carrying a sack on his back, walking away."],
    ['आणे', 'aane', "A girl bringing a basket of fruit towards the viewer."],
    ['हो', 'ho', "A lotus bud opening into a full bloom on a pond."]
]

const BY_ROOT = new Map<string, DhatuPictureEntry>(ROWS.map(([root, file, scene]) => [root, { src: `/images/dhatu/${file}.jpg`, scene }]))

/** The picture of a root, or null when it has none (अस, or a root an editor added). */
export const dhatuPicture = (root: string): DhatuPictureEntry | null => BY_ROOT.get(root.normalize('NFC')) ?? null
